import { State, Action, StateContext, Selector, createSelector } from '@ngxs/store';
import * as _ from 'lodash';
import { Approval } from 'ng2-app/models/dto/approval/approval.model';
import { GetApprovals,
  GetApprovalsCount,
  SetApprovalCalculator,
  GetApprovalCalculatorStorage,
  UpdatePublishApproval,
  UpdateUnpublishApproval,
} from './approvals.actions';
import { ApprovalsService } from 'app/services/approvals/approvals.service';
import {catchError, take, tap} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { differenceInDays } from 'date-fns';
import {of} from 'rxjs';
import { patch, updateItem } from '@ngxs/store/operators';

export class ApprovalsStateModel {
  approvals: Approval[];
  approvalsCount: number;
  adjustedApproval: Approval | null;
  approvalCalculators: ApprovalCalculatorStateModel[];
  loading: boolean;
}

export interface ApprovalCalculatorStateModel {
  approvalId;
  term: number;
  paymentFrequency: string;
  points: number;
  amount: number;
  originationPercent: number;
  lastUpdated: string;
}

@State<ApprovalsStateModel>({
  name: 'Approvals',
  defaults: {
    approvals: [],
    approvalsCount: 0,
    adjustedApproval: null,
    approvalCalculators: [],
    loading: false,
  },
})
@Injectable()
export class ApprovalsState {
  @Selector()
  static approvals(state: ApprovalsStateModel) {
    return state.approvals;
  }

  @Selector()
  static approvalsCount(state: ApprovalsStateModel) {
    return state.approvalsCount;
  }

  @Selector()
  static adjustedApproval(state: ApprovalsStateModel) {
    return state.adjustedApproval;
  }

  @Selector()
  static approvalCalculator(approvalId: number) {
    return createSelector([ApprovalsState], (state) => {
      return (
        state.Approvals.approvalCalculators.filter(
          (calc) => calc.approvalId === approvalId
        )[0] || false
      );
    });
  }

  // approvals by dealId  - how to use:
  // this.approvals$ = this.store.select(ApprovalsState.forDeal).pipe(map(filterFn => filterFn(this.deal.id)));
  @Selector()
  static forDeal(state: ApprovalsStateModel) {
    return (dealId: number) => {
      return state.approvals?.filter((approval) => approval.dealId === dealId);
    };
  }

  @Selector()
  static highestDealApproval(state: ApprovalsStateModel) {
    return (dealId: number) => {
      const allApprovals = state.approvals?.filter((a) => a.dealId === dealId);
      const sortedApprovals = allApprovals?.sort((a, b) => {
        let bTermMax = 0;
        let aTermMax = 0;
        let bRate = 100;
        let aRate = 100;
        b.options.forEach((o) => {
          if (o.term > bTermMax) {
            bTermMax = o.term;
            bRate = o.buyRate;
          }
        });
        a.options.forEach((o) => {
          if (o.term > aTermMax) {
            aTermMax = o.term;
            aRate = o.buyRate;
          }
        });
        if (bTermMax !== aTermMax) {
          return bTermMax - aTermMax;
        } else {
          return aRate - bRate;
        }
      });
      return sortedApprovals?.[0];
    };
  }

  @Selector()
  static loading(state: ApprovalsStateModel) {
    return state.loading;
  }

  calculatorStateTTLDays = 14;

  constructor(private approvalsService: ApprovalsService) {}

  @Action(GetApprovals)
  getApprovals(
    { patchState }: StateContext<ApprovalsStateModel>,
    { borrowerId }: GetApprovals
  ) {
    patchState({
      approvals: [],
      loading: true,
    });
    this.approvalsService
      .getApprovalsByBorrowerId(borrowerId)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(error);
          return of();
        })
      )
      .subscribe((unsortedApprovals: Approval[]) => {
        const approvals = unsortedApprovals?.sort(this.approvalsSort);
        patchState({
          approvals,
          loading: false,
        });
      });
  }

  @Action(GetApprovalsCount)
  getApprovalsCount(
    { getState, patchState }: StateContext<ApprovalsStateModel>,
    { borrowerId }: GetApprovalsCount
  ) {
    patchState({ approvalsCount: 0 });
    this.approvalsService
      .getApprovalsCountByBorrowerId(borrowerId)
      .pipe(
        take(1),
        catchError((error) => {
          console.error(error);
          return of();
        })
      )
      .subscribe((res) => {
        const approvalsCount = res;
        if (approvalsCount && typeof approvalsCount === 'number') {
          patchState({ approvalsCount });
        }
      });
  }

  @Action(SetApprovalCalculator)
  setApprovalCalculator(
    { getState, patchState }: StateContext<ApprovalsStateModel>,
    { approvalCalculatorState }: SetApprovalCalculator
  ) {
    const state = getState();
    const rest = state.approvalCalculators.filter(
      (calc) => calc.approvalId !== approvalCalculatorState.approvalId
    );
    patchState({
      approvalCalculators: [...rest, approvalCalculatorState],
    });
    this.setApprovalCalculatorsStorage(getState());
  }

  @Action(GetApprovalCalculatorStorage)
  getApprovalCalculatorsStorage({
    patchState,
  }: StateContext<ApprovalsStateModel>) {
    const storageVal = JSON.parse(
      localStorage.getItem('approvalCalculatorsState') || '[]'
    );
    const now = new Date();
    const prunedStorage = storageVal.filter((calc) => {
      const calcLastUpdated = new Date(calc.lastUpdated);
      return (
        differenceInDays(calcLastUpdated, now) < this.calculatorStateTTLDays
      );
    });
    patchState({
      approvalCalculators: prunedStorage,
    });
  }

  @Action(UpdatePublishApproval)
  updatePublishApproval(
    { setState }: StateContext<ApprovalsStateModel>,
    { approvalId }: { approvalId: number }
  ) {
    return this.approvalsService.publishApproval(approvalId).pipe(
      tap((publishedApproval) => {
        setState(
          patch<ApprovalsStateModel>({
            approvals: updateItem(
              (approval) => {
                const isApprovalMatch = approval && approval.id === approvalId;
                const isPublishedApprovalValuesSet = publishedApproval.publishedByLenderUserId && publishedApproval.publishedToBorrower;
                return isApprovalMatch && !!isPublishedApprovalValuesSet;
              },
              patch<Approval>({
                publishedByLenderUserId: publishedApproval.publishedByLenderUserId,
                publishedToBorrower: publishedApproval.publishedToBorrower,
              })
            ),
          })
        );
      })
    );
  }

  @Action(UpdateUnpublishApproval)
  updateUnpublishApproval(
    { setState }: StateContext<ApprovalsStateModel>,
    { approvalId }: { approvalId: number }
  ) {
    return this.approvalsService.unpublishApproval(approvalId).pipe(
      tap((publishedApproval) => {
        setState(
          patch<ApprovalsStateModel>({
            approvals: updateItem(
              (approval) => approval && approval.id === approvalId,
              patch<Approval>({
                publishedByLenderUserId: publishedApproval.publishedByLenderUserId,
                publishedToBorrower: publishedApproval.publishedToBorrower,
              })
            ),
          })
        );
      })
    );
  }

  setApprovalCalculatorsStorage(state: ApprovalsStateModel) {
    const storageVal = JSON.stringify(state.approvalCalculators);
    localStorage.setItem('approvalCalculatorsState', storageVal);
  }

  pusherSubscribe(channel: string) {}

  pusherUnsubscribe(dealId: number) {}

  approvalsSort(a, b) {
    const nameA = String(a.loanProductLender).toLowerCase();
    const nameB = String(b.loanProductLender).toLowerCase();
    const typeA = String(a.loanProductType).toLowerCase();
    const typeB = String(b.loanProductType).toLowerCase();

    if (nameA > nameB) {
      return 1;
    }
    if (nameA < nameB) {
      return -1;
    }
    if (typeA > typeB) {
      return -1;
    }
    if (typeA < typeB) {
      return 1;
    }
    return 1;
  }
}
